import { create, type StateCreator } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { getGame } from '@/shared/api/game';
import { type GameOrientation } from '@/shared/api/game/types';
import { type DecodedToken } from '@/shared/api/types';
import { getUserProfile } from '@/shared/api/user';
import { AD_NETWORK, type WalletType } from '@/shared/api/user/types';
import { ErrorLogger } from '@/shared/service/logger/ErrorLogger';

import { DEFAULT_APP_STATE } from './constants';
import { type AppState } from './types';
import { resolveGameUrl } from './utils/resolve-game-url';

const middlewares = (f: StateCreator<AppState, [['zustand/persist', unknown]], []>) =>
  devtools(
    persist(f, {
      name: 'persist',
      partialize: (state: AppState) => ({ token: state.token }),
    })
  );

export const useAppStateRaw = create<AppState>()(
  middlewares((set, get) => ({
    ...DEFAULT_APP_STATE,
    actions: {
      hydrate: (fresh) => set(fresh),
      reset: () => set((prev: AppState) => ({ ...prev, ...DEFAULT_APP_STATE })),
      setIsWebMode: (value: boolean) => set((prev) => ({ ...prev, isWebMode: value })),
      setIsLiteMode: (value: boolean) => set((prev) => ({ ...prev, isLiteMode: value })),
      setScreenOrientation: (orientation: GameOrientation) => {
        set({ screenOrientation: orientation });
      },
      setIsExclusiveMode: (value: boolean) =>
        set((prev) => ({ ...prev, isExclusiveMode: value })),
      loadGame: async (decodedToken: DecodedToken) => {
        try {
          const game = await getGame(decodedToken);
          const isGameLoading = false;

          const resolvedGameUrl = resolveGameUrl(game.gameUrl, get().lang.game);

          set((prev) => ({
            ...prev,
            isGameLoading,
            game,
            gameUrl: resolvedGameUrl,
          }));
        } catch (error) {
          ErrorLogger.error(error as Error, 'loadGame', {
            sid: get().sessionId,
            game: String(decodedToken?.gameKey),
          });
        }
      },
      fetchUserProfile: async () => {
        try {
          const userProfile = await getUserProfile();
          const isLiteMode = get().isLiteMode;
          const isExclusiveMode = get().isExclusiveMode;
          const user = isLiteMode
            ? userProfile
            : {
                ...userProfile,
                avdNetworks: userProfile.avdNetworks.filter((network) =>
                  isExclusiveMode ? network !== AD_NETWORK.AG : true
                ),
              };

          set((prev) => {
            const wasNoAd = prev.userProfile?.tests?.noAd === true;
            const nextTests = {
              ...user.tests,
              noAd: user.tests?.noAd === true || wasNoAd,
            };
            return { userProfile: { ...user, tests: nextTests } };
          });
        } catch (error) {
          ErrorLogger.error(error as Error, 'fetchUserProfile', {
            sid: get().sessionId,
          });
        }
      },
      markNoAdPurchased: () =>
        set((prev) => {
          if (!prev.userProfile) return prev;
          return {
            userProfile: {
              ...prev.userProfile,
              tests: { ...prev.userProfile.tests, noAd: true },
            },
          };
        }),
    },
  }))
);

// GROUPED SELECTORS
export const useAppActions = () => useAppStateRaw((state) => state.actions);
// use shallow comparison to eliminate unnecessary re-renders
// you have to consume this selector instead of useAppStateRaw in components!
export const useAppState = () =>
  useAppStateRaw(
    useShallow((state) => ({
      token: state.token,
      sessionId: state.sessionId,
      decodedToken: state.decodedToken,
      gameUrl: state.gameUrl,
      game: state.game,
      isGameLoading: state.isGameLoading,
      userProfile: state.userProfile,
      lang: state.lang,
      theme: state.theme,
      startAppArgs: state.startAppArgs,
      showDevMenu: state.showDevMenu,
      actions: state.actions,
      wsSentInitialMsg: state.wsSentInitialMsg,
      messages: state.messages,
      isWebMode: state.isWebMode,
      isLiteMode: state.isLiteMode,
      screenOrientation: state.screenOrientation,
      isExclusiveMode: state.isExclusiveMode,
      pdTma: state.pdTma,
    }))
  );

// ATOMIC SELECTORS
export const useGameUrl = () => useAppStateRaw(useShallow((state) => state.gameUrl));
export const useToken = () => useAppStateRaw(useShallow((state) => state.token));
export const useSessionId = () => useAppStateRaw(useShallow((state) => state.sessionId));
export const useDecodedToken = () =>
  useAppStateRaw(useShallow((state) => state.decodedToken));
export const useGame = () => useAppStateRaw(useShallow((state) => state.game));
export const useIsWebMode = () => useAppStateRaw(useShallow((state) => state.isWebMode));
export const useGameLoading = () =>
  useAppStateRaw(useShallow((state) => state.isGameLoading));
export const useUserProfile = () =>
  useAppStateRaw(useShallow((state) => state.userProfile));
export const useLang = () => useAppStateRaw(useShallow((state) => state.lang));
export const useWallet = (type: WalletType) =>
  useAppStateRaw(
    useShallow((state) => state.userProfile?.wallets?.find((w) => w.type === type))
  );
export const useStartAppArgs = () =>
  useAppStateRaw(useShallow((state) => state.startAppArgs));
export const usePdTma = () => useAppStateRaw(useShallow((state) => state.pdTma));
// Для не-React контекстов (хендлеры, сервисы), где хук вызвать нельзя.
export const getPdTma = (): string | null => useAppStateRaw.getState().pdTma;
